import { Link } from 'react-router';
import { isApiError } from '@/api/errors';
import { Badge } from '@/components/Badge';
import { BillSummary } from '@/components/BillSummary';
import type { BillRow } from '@/components/BillSummary';
import { buttonClasses } from '@/components/Button';
import { cardClasses } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Money } from '@/components/Money';
import { cn } from '@/lib/cn';
import { useCustomer } from './context';
import { ListSkeleton, PageTitle, Panel, PartyLine, formatTime } from './parts';
import { useBill } from './queries';
import { orderTitle, paymentStatusLabel, settlementView, tenderLabel } from './wording';

/**
 * The bill is the financial authority and is separate from Orders. Every figure is the server's:
 * nothing is added, subtracted or rounded here. Paying is not part of this phase.
 */
export function BillPage() {
  const bill = useBill({ poll: true });
  const { money } = useCustomer();

  const party = <PartyLine />;

  if (bill.isPending) {
    return (
      <>
        <PageTitle subtitle={party}>Bill</PageTitle>
        <ListSkeleton rows={4} />
      </>
    );
  }

  if (bill.isError && !bill.data) {
    if (isApiError(bill.error) && bill.error.hasCode('BILL_NOT_FOUND')) {
      return (
        <>
          <PageTitle subtitle={party}>Bill</PageTitle>
          <EmptyState
            icon="bill"
            title="No bill yet"
            message="Your bill starts with your first order."
            action={
              <Link to="/t/menu" className={buttonClasses({ variant: 'primary' })}>
                Browse the menu
              </Link>
            }
          />
        </>
      );
    }
    return (
      <>
        <PageTitle subtitle={party}>Bill</PageTitle>
        <ErrorState error={bill.error} onRetry={() => void bill.refetch()} />
      </>
    );
  }

  const data = bill.data;
  if (!data) return null;
  const settlement = settlementView(data.settlement_status);
  const settled = data.remaining_balance === 0;
  // Zero-value rows say nothing to a guest; only rows with an amount are shown.
  const rows: BillRow[] = [];
  if (data.tax_amount !== 0) rows.push({ label: 'Tax', amount: money(data.tax_amount) });
  if (data.service_charge_amount !== 0) {
    rows.push({ label: 'Service charge', amount: money(data.service_charge_amount) });
  }
  const paidSomething = data.paid_amount !== 0;

  return (
    <>
      <PageTitle subtitle={party} aside={<Badge tone={settlement.tone}>{settlement.label}</Badge>}>
        Bill
      </PageTitle>
      <div className="flex flex-col gap-4">
        {data.items.length > 0 && (
          <Panel>
            <ul className="divide-y divide-line" aria-label="Billed items">
              {data.items.map((item) => (
                <li
                  key={item.order_item_id}
                  className="flex items-start justify-between gap-3 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-base font-semibold text-ink">
                      {item.name}{' '}
                      <span className="font-medium text-muted tabular-nums">×{item.quantity}</span>
                    </p>
                    {item.modifiers.length > 0 && (
                      <p className="text-sm text-muted">{item.modifiers.join(', ')}</p>
                    )}
                    <p className="text-xs text-faint">{orderTitle(item.order_number)}</p>
                  </div>
                  <Money
                    value={money(item.line_amount)}
                    className="shrink-0 text-base font-bold text-ink"
                  />
                </li>
              ))}
            </ul>
          </Panel>
        )}

        <BillSummary
          subtotal={money(data.subtotal_amount)}
          taxes={rows}
          discount={money(data.discount_amount)}
          total={money(data.total_amount)}
        />

        <dl
          className={cn(
            cardClasses(),
            'text-sm',
            settled ? 'border-success bg-success-soft' : 'border-attention-line bg-attention-soft',
          )}
        >
          {paidSomething && (
            <div className="flex justify-between py-1 text-muted">
              <dt>Paid</dt>
              <dd>
                <Money value={money(data.paid_amount)} />
              </dd>
            </div>
          )}
          <div
            className={cn(
              'flex items-baseline justify-between text-base font-bold',
              paidSomething && 'mt-2 border-t pt-3',
              settled
                ? 'border-success/30 text-success'
                : 'border-attention-line text-attention-ink',
            )}
          >
            <dt>Balance due</dt>
            <dd className="text-xl">
              <Money value={money(data.remaining_balance)} />
            </dd>
          </div>
        </dl>

        {data.payments.length > 0 && (
          <section aria-labelledby="payments-heading">
            <h2 id="payments-heading" className="mb-2 text-lg font-bold text-ink">
              Payments
            </h2>
            <Panel>
              <ul className="divide-y divide-line">
                {data.payments.map((payment) => (
                  <li
                    key={payment.payment_id}
                    className="flex items-center justify-between gap-3 px-4 py-3 text-sm"
                  >
                    <span>
                      <span className="block font-semibold text-ink">
                        {tenderLabel(payment.tender_type)} · {paymentStatusLabel(payment.status)}
                      </span>
                      <span className="block text-muted">{formatTime(payment.created_at)}</span>
                    </span>
                    <Money value={money(payment.amount)} className="font-bold text-ink" />
                  </li>
                ))}
              </ul>
            </Panel>
          </section>
        )}

        <Link to="/t/help" className={buttonClasses({ variant: 'secondary', fullWidth: true })}>
          Ask for the bill
        </Link>
      </div>
    </>
  );
}
